import React from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Menu, 
  X, 
  ArrowRight, 
  CheckCircle2, 
  ChevronRight,
  Clock,
  Utensils,
  Users,
  Sun
} from 'lucide-react';
import { data } from './data';
import OmOs from './pages/OmOs';
import Mindehoejtidelighed from './pages/Mindehoejtidelighed';
import Kontakt from './pages/Kontakt';
import Bryllup from './pages/Bryllup';
import Konfirmation from './pages/Konfirmation';
import Barnedaab from './pages/Barnedaab';
import Konference from './pages/Konference';
import EventkalenderOgGalleri from './pages/EventkalenderOgGalleri';

// --- Components ---

const Navbar = () => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <nav className="fixed w-full z-50 transition-all duration-300 bg-[#081f32] border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col py-4 md:py-6">
          {/* Top Row: Logo & Desktop Button / Mobile Toggle */}
          <div className="flex justify-between items-center w-full">
            <Link to="/" className="flex-shrink-0">
              <img className="h-10 md:h-12 w-auto" style={{ filter: 'brightness(0) saturate(100%) invert(77%) sepia(43%) saturate(618%) hue-rotate(357deg) brightness(91%) contrast(86%)' }} src={data.logo} alt="GuldBoSund Konference & Event" />
            </Link>
            
            <div className="flex items-center space-x-4">
              {/* Desktop Button (Shown on md and up) */}
              <Link to="/kontakt" className="hidden md:flex bg-[#dab256] text-[#081f32] px-6 py-2.5 rounded-full hover:bg-[#c4a04a] transition-colors font-light uppercase tracking-widest text-[11px]">
                Book nu
              </Link>
              
              {/* Mobile Menu Toggle */}
              <div className="md:hidden">
                <button onClick={() => setIsOpen(!isOpen)} className="text-white p-2">
                  {isOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
              </div>
            </div>
          </div>

          {/* Bottom Row: Links (Shown on md and up) */}
          <div className="hidden md:flex items-center justify-start space-x-10 mt-6 pt-4 border-t border-white/5">
            <Link to="/" className="text-white/80 hover:text-white font-light uppercase tracking-[0.2em] text-[11px] transition-colors">Forside</Link>
            <Link to="/bryllup" className="text-white/80 hover:text-white font-light uppercase tracking-[0.2em] text-[11px] transition-colors">Bryllup</Link>
            <Link to="/konfirmation" className="text-white/80 hover:text-white font-light uppercase tracking-[0.2em] text-[11px] transition-colors">Konfirmation</Link>
            <Link to="/mindehoejtidelighed" className="text-white/80 hover:text-white font-light uppercase tracking-[0.2em] text-[11px] transition-colors">Mindehøjtidelighed</Link>
            <Link to="/konference" className="text-white/80 hover:text-white font-light uppercase tracking-[0.2em] text-[11px] transition-colors">Konference</Link>
            <Link to="/eventkalender-og-galleri" className="text-white/80 hover:text-white font-light uppercase tracking-[0.2em] text-[11px] transition-colors">Eventkalender & Galleri</Link>
            <Link to="/om-os" className="text-white/80 hover:text-white font-light uppercase tracking-[0.2em] text-[11px] transition-colors">Om Os</Link>
            <Link to="/kontakt" className="text-white/80 hover:text-white font-light uppercase tracking-[0.2em] text-[11px] transition-colors">Kontakt</Link>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#081f32] border-b border-white/10 overflow-hidden"
          >
            <div className="px-4 pt-2 pb-6 space-y-1">
              <Link to="/" onClick={() => setIsOpen(false)} className="block px-3 py-4 text-base font-light text-white hover:bg-white/5 rounded-lg uppercase tracking-widest">Forside</Link>
              <Link to="/bryllup" onClick={() => setIsOpen(false)} className="block px-3 py-4 text-base font-light text-white hover:bg-white/5 rounded-lg uppercase tracking-widest">Bryllup</Link>
              <Link to="/konfirmation" onClick={() => setIsOpen(false)} className="block px-3 py-4 text-base font-light text-white hover:bg-white/5 rounded-lg uppercase tracking-widest">Konfirmation</Link>
              <Link to="/mindehoejtidelighed" onClick={() => setIsOpen(false)} className="block px-3 py-4 text-base font-light text-white hover:bg-white/5 rounded-lg uppercase tracking-widest">Mindehøjtidelighed</Link>
              <Link to="/konference" onClick={() => setIsOpen(false)} className="block px-3 py-4 text-base font-light text-white hover:bg-white/5 rounded-lg uppercase tracking-widest">Konference</Link>
              <Link to="/eventkalender-og-galleri" onClick={() => setIsOpen(false)} className="block px-3 py-4 text-base font-light text-white hover:bg-white/5 rounded-lg uppercase tracking-widest">Eventkalender & Galleri</Link>
              <Link to="/om-os" onClick={() => setIsOpen(false)} className="block px-3 py-4 text-base font-light text-white hover:bg-white/5 rounded-lg uppercase tracking-widest">Om Os</Link>
              <Link to="/kontakt" onClick={() => setIsOpen(false)} className="block px-3 py-4 text-base font-light text-[#081f32] bg-[#dab256] rounded-lg uppercase tracking-widest">Kontakt</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

const Footer = () => (
  <footer className="bg-[#081f32] text-stone-300 py-16 border-t border-white/5">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-4 gap-12">
      <div className="col-span-1 md:col-span-1">
        <img 
          src={data.logo} 
          alt="Logo" 
          className="h-12 w-auto mb-6" 
          style={{ filter: 'brightness(0) saturate(100%) invert(77%) sepia(43%) saturate(618%) hue-rotate(357deg) brightness(91%) contrast(86%)' }}
        />
        <p className="text-sm leading-relaxed">
          GuldBoSund Konference & Event er Lolland-Falsters mest stemningsfulde ramme for livets store begivenheder.
        </p>
      </div>
      <div>
        <h4 className="text-white font-light mb-6 uppercase tracking-widest text-xs">Menu</h4>
        <ul className="space-y-4 text-xs font-light uppercase tracking-widest">
          <li><Link to="/bryllup" className="hover:text-white transition-colors">Bryllup</Link></li>
          <li><Link to="/konfirmation" className="hover:text-white transition-colors">Konfirmation & Barnedåb</Link></li>
          <li><Link to="/mindehoejtidelighed" className="hover:text-white transition-colors">Mindehøjtidelighed</Link></li>
          <li><Link to="/konference" className="hover:text-white transition-colors">Konference</Link></li>
          <li><Link to="/eventkalender-og-galleri" className="hover:text-white transition-colors">Eventkalender & Galleri</Link></li>
          <li><Link to="/om-os" className="hover:text-white transition-colors">Om Os</Link></li>
          <li><Link to="/kontakt" className="hover:text-white transition-colors">Kontakt</Link></li>
        </ul>
      </div>
      <div>
        <h4 className="text-white font-light mb-6 uppercase tracking-widest text-xs">Kontakt</h4>
        <ul className="space-y-4 text-sm">
          <li className="flex items-center gap-3"><Phone size={16} className="text-white/60" /> {data.contact.phone}</li>
          <li className="flex items-center gap-3"><Mail size={16} className="text-white/60" /> {data.contact.email}</li>
          <li className="flex items-center gap-3"><MapPin size={16} className="text-white/60" /> {data.contact.address}</li>
        </ul>
      </div>
      <div>
        <h4 className="text-white font-bold mb-6 uppercase tracking-widest text-xs">Følg os</h4>
        <div className="flex gap-4">
          <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#dab256] transition-colors cursor-pointer text-white">
            <span className="sr-only">Facebook</span>
            <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"></path></svg>
          </div>
        </div>
      </div>
    </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 pt-8 border-t border-white/10 text-xs text-center text-white/40">
      © {new Date().getFullYear()} GuldBoSund Konference & Event. Alle rettigheder forbeholdes.
    </div>
  </footer>
);

const ContactForm = () => {
  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const payload = Object.fromEntries(formData.entries());
    
    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        alert('Tak for din henvendelse. Vi kontakter dig snarest!');
        e.target.reset();
      }
    } catch (error) {
      console.error('Submission error:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 bg-[#081f32] p-6 md:p-8 rounded-none sm:rounded-2xl shadow-xl border-y sm:border border-white/10">
      <div className="flex justify-start mb-6">
        <img src="/Kundebilleder/imgi_11_Logo-Side-No-BG.png" alt="GuldBoSund Konference & Event Logo" className="h-16 w-auto brightness-0 invert" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-white/80 mb-1">Navn</label>
          <input required name="name" type="text" className="w-full px-4 py-3 rounded-lg border border-white/10 bg-white/5 text-white focus:ring-2 focus:ring-[#dab256] focus:border-transparent outline-none transition-all" />
        </div>
        <div>
          <label className="block text-sm font-medium text-white/80 mb-1">Email</label>
          <input required name="email" type="email" className="w-full px-4 py-3 rounded-lg border border-white/10 bg-white/5 text-white focus:ring-2 focus:ring-[#dab256] focus:border-transparent outline-none transition-all" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-white/80 mb-1">Telefon</label>
        <input required name="phone" type="tel" className="w-full px-4 py-3 rounded-lg border border-white/10 bg-white/5 text-white focus:ring-2 focus:ring-[#dab256] focus:border-transparent outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-medium text-white/80 mb-1">Besked</label>
        <textarea required name="message" rows="4" className="w-full px-4 py-3 rounded-lg border border-white/10 bg-white/5 text-white focus:ring-2 focus:ring-[#dab256] focus:border-transparent outline-none transition-all"></textarea>
      </div>
      
      {/* Honeypot */}
      <div className="sr-only absolute" aria-hidden="true">
        <input type="text" name="website_url" tabIndex="-1" autoComplete="off" />
      </div>

      <button type="submit" className="w-full bg-[#dab256] text-[#081f32] py-4 rounded-lg text-xs font-light uppercase tracking-widest hover:bg-[#c4a04a] transition-colors shadow-lg flex items-center justify-center gap-2">
        Send Forespørgsel <ArrowRight size={20} />
      </button>
    </form>
  );
};

// --- Pages ---

const Home = () => {
  return (
    <div className="scroll-smooth">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0 bg-black">
          <video 
            autoPlay 
            muted 
            loop 
            playsInline
            className="w-full h-full object-cover"
            poster={data.hero.image}
          >
            <source src="/video/shutterstock_3944111187.mp4" type="video/mp4" />
            Din browser understøtter ikke video-tagget.
          </video>
          <div className="absolute inset-0 bg-black/40 z-10" />
        </div>
        
        <div className="relative z-10 text-center text-white px-4 max-w-4xl">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="text-5xl md:text-7xl font-light mb-6 leading-tight uppercase tracking-wider"
          >
            {data.hero.title}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.2, ease: "easeOut" }}
            className="text-xl md:text-2xl mb-10 text-gray-100 font-light"
          >
            {data.hero.subtitle}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.4, ease: "easeOut" }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link to="/kontakt" className="bg-[#dab256] hover:bg-[#c4a04a] text-[#081f32] px-10 py-4 rounded-full text-sm font-light uppercase tracking-widest transition-all transform hover:scale-105 shadow-xl text-center">
              {data.hero.cta}
            </Link>
            <Link to="/konference" className="bg-transparent border border-white text-white px-10 py-4 rounded-full text-sm font-light uppercase tracking-widest transition-all hover:bg-white/10 text-center">
              Se Konferencepakker
            </Link>
          </motion.div>
        </div>

        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 1 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce"
        >
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-white rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Event Cards */}
      <motion.section 
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1 }}
        className="py-16 bg-stone-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-light text-gray-900 mb-4 uppercase tracking-wider">Planlæg jeres næste store dag</h2>
            <p className="text-xl text-gray-600">Vi har pakker der passer til enhver lejlighed</p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
            {data.services.map((service, index) => {
              const isMiddle = index === 1;
              return (
                <motion.div 
                  key={service.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  whileHover={{ y: -10 }}
                  className={`bg-white rounded-3xl overflow-hidden transition-all duration-300 ${
                    isMiddle 
                      ? 'shadow-2xl md:scale-105 z-10 -translate-y-2' 
                      : 'shadow-lg border border-gray-100'
                  } group`}
                >
                  <div className="h-64 overflow-hidden relative">
                    <img src={service.image} className={`w-full h-full object-cover transition-transform duration-500 ${isMiddle ? 'scale-110' : 'group-hover:scale-110'}`} alt={service.title} />
                    <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-2xl text-gray-900 font-light border border-gray-100 shadow-sm">
                      <span className="text-sm uppercase tracking-wider block text-gray-400 leading-none mb-1">Fra kun</span>
                      <span className="text-xl font-medium tracking-tight leading-none">{service.price} <span className="text-sm text-gray-500 font-light lowercase">pr. kuvert</span></span>
                    </div>
                  </div>
                  <div className="p-8 flex flex-col h-[calc(100%-16rem)]">
                    <h3 className="text-2xl font-light mb-4 uppercase tracking-wider">{service.title}</h3>
                    <p className="text-gray-600 mb-6 flex-grow">{service.description}</p>
                    <ul className="space-y-3 mb-8">
                      {service.features.slice(0, 4).map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                          <CheckCircle2 size={16} className="text-gray-300 mt-0.5 flex-shrink-0" /> 
                          <span className="leading-tight">{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Link 
                      to={`/${service.id}`} 
                      className={`block text-center py-4 rounded-xl font-medium transition-all uppercase tracking-widest text-xs ${
                        isMiddle 
                          ? 'bg-[#081f32] text-white' 
                          : 'border-2 border-gray-100 text-gray-900 hover:bg-[#081f32] hover:text-white hover:border-[#081f32]'
                      }`}
                    >
                      Se Pakken
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.section>

      {/* Intro Section - Nature & Food focus */}
      <motion.section 
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1, ease: "easeOut" }}
        className="py-16 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <span className="text-gray-400 font-medium tracking-widest uppercase text-sm mb-4 block">Uforglemmelige omgivelser</span>
              <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-8 leading-tight uppercase tracking-wider">
                Mærk roen fra Guldborgsund og nyd gastronomien
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Hos GuldBoSund Konference & Event er vi stolte af vores unikke placering. Vi kombinerer det bedste fra den danske natur med et køkken, der hylder de lokale råvarer og det gode håndværk. Uanset om I planlægger en intim fest eller en stor konference, danner naturen den perfekte baggrund.
              </p>
              <div className="grid grid-cols-2 gap-6 mb-10">
                {[
                  "Panoramaudsigt til vandet",
                  "Lokale råvarer i sæson",
                  "Arkitektoniske rammer",
                  "Professionel service"
                ].map((item, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 * i + 0.5 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 className="text-gray-300 flex-shrink-0" />
                    <span className="font-medium text-gray-700">{item}</span>
                  </motion.div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4">
                <Link to="/om-os" className="bg-[#dab256] text-[#081f32] px-8 py-3 rounded-full text-xs font-light uppercase tracking-widest hover:bg-[#c4a04a] transition-all">
                  Læs mere om os
                </Link>
                <Link to="/kontakt" className="border border-[#081f32] text-[#081f32] px-8 py-3 rounded-full text-xs font-light uppercase tracking-widest hover:bg-[#081f32] hover:text-white transition-all">
                  Book nu
                </Link>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative"
            >
              <img src="/Kundebilleder/d7525ece-6de0-46c0-b5ff-cc6d065f74aa-1_all_6266.jpg" className="rounded-3xl shadow-2xl w-full h-[600px] object-cover" alt="Gastronomi på GuldBoSund Konference & Event" />
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="absolute -bottom-6 -right-6 bg-[#dab256] text-[#081f32] p-8 rounded-2xl hidden md:block max-w-[200px] shadow-2xl"
              >
                <p className="text-3xl font-light mb-1">100%</p>
                <p className="text-[10px] opacity-80 uppercase tracking-widest font-medium">Dedikeret til jeres event</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Scenic Frames Section */}
      <motion.section 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1 }}
        className="py-16 bg-white overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 relative">
              <motion.div 
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, ease: "easeOut" }}
              >
                <img src="/images/nature-view.png" className="rounded-3xl shadow-2xl w-full h-[600px] object-cover" alt="Naturskønne rammer på GuldBoSund Konference & Event" />
              </motion.div>
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-[#081f32]/5 rounded-full -z-10" />
            </div>
            <div className="order-1 lg:order-2">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
              >
                <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-8 leading-tight uppercase tracking-wider">
                  Naturskønne rammer, skræddersyet til jeres arrangement
                </h2>
                <div className="space-y-6 text-lg text-gray-600 leading-relaxed font-light">
                  <p>
                    GuldBoSund Konference & Event tilbyder en unik kombination af komfort og service i topklasse. Vores faciliteter rummer alt fra intime møderum til et auditorium med plads til 350 gæster, alt sammen indrammet af den smukke natur ved Guldborgsund.
                  </p>
                  <p>
                    Vi skræddersyr ethvert arrangement med fokus på gastronomiske oplevelser og personlig rådgivning. Uanset om det gælder konferencer eller private fester, sørger vores team for, at rammerne er perfekte, så I kan fokusere på jeres gæster.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Corporate Section Highlight */}
      <motion.section 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1.2 }}
        className="relative py-24 overflow-hidden bg-[#081f32] text-white"
      >
        <div className="absolute inset-0 opacity-20">
          <motion.img 
            initial={{ scale: 1.1 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 2, ease: "easeOut" }}
            src={data.corporate.image} 
            className="w-full h-full object-cover" 
            alt="Konference" 
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2 }}
            className="max-w-2xl"
          >
            <h2 className="text-4xl md:text-5xl font-light mb-6 uppercase tracking-wider">{data.corporate.title}</h2>
            <p className="text-xl text-stone-300 mb-10 leading-relaxed">
              Professionelle konferencelokaler kombineret med roen fra fjorden. Den ideelle ramme for strategimøder, workshops og firmafester.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
              {data.corporate.features.map((f, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 + (i * 0.1) }}
                  className="flex items-center gap-3"
                >
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                    <ChevronRight size={18} />
                  </div>
                  <span className="font-medium">{f}</span>
                </motion.div>
              ))}
            </div>
            <Link to="/konference" className="inline-block bg-[#dab256] text-[#081f32] px-8 py-4 rounded-full text-xs font-light uppercase tracking-widest hover:bg-[#c4a04a] transition-colors shadow-lg">
              Udforsk erhvervsmuligheder
            </Link>
          </motion.div>
        </div>
      </motion.section>

      {/* Contact Section */}
      <motion.section 
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1 }}
        className="py-16 bg-white" 
        id="kontakt"
      >
        <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="px-4 sm:px-0"
            >
              <h2 className="text-4xl font-light mb-6 text-gray-900 uppercase tracking-wider">Lad os planlægge jeres næste event sammen</h2>
              <p className="text-lg text-gray-600 mb-10">
                Udfyld formularen eller giv os et kald. Vi står altid klar til en uforpligtende snak om dine ønsker og behov.
              </p>
              
              <div className="space-y-6">
                {[
                  { icon: Phone, label: "Ring til os", value: data.contact.phone },
                  { icon: Mail, label: "Email", value: data.contact.email },
                  { icon: MapPin, label: "Adresse", value: data.contact.address }
                ].map((item, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 + (i * 0.1) }}
                    className="flex items-center gap-4 group"
                  >
                    <div className="w-12 h-12 rounded-full bg-[#081f32] flex items-center justify-center transition-transform group-hover:scale-110">
                      <item.icon className="text-white" size={20} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 uppercase font-medium tracking-wider">{item.label}</p>
                      <p className="text-xl font-medium text-[#081f32]">{item.value}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <ContactForm />
            </motion.div>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

const App = () => {
  const location = useLocation();

  return (
    <div className="font-sans text-stone-900 selection:bg-[#dab256]/30 selection:text-black">
      <Navbar />
      <main>
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={<Home />} />
              <Route path="/bryllup" element={<Bryllup />} />
              <Route path="/konfirmation" element={<Konfirmation />} />
              <Route path="/barnedaab" element={<Barnedaab />} />
              <Route path="/konference" element={<Konference />} />
              <Route path="/eventkalender-og-galleri" element={<EventkalenderOgGalleri />} />
              <Route path="/mindehoejtidelighed" element={<Mindehoejtidelighed />} />
              <Route path="/om-os" element={<OmOs />} />
              <Route path="/kontakt" element={<Kontakt />} />
            </Routes>
          </motion.div>
        </AnimatePresence>
      </main>
      <Footer />
    </div>
  );
};

export default App;
